import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { DEFAULT_CALENDAR, SUPPORTED_TIMEZONES } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Field } from '../../../../components/Field';
import { Input } from '../../../../components/Input';
import { Popover, usePopover } from '../../../../components/Popover';
import { Select } from '../../../../components/Select';
import { TimePicker } from '../../../../components/TimePicker';
import { WorkingDayPicker } from '../../../../components/WorkingDayPicker';
import { useToasts } from '../../../../stores/toasts';
import { useDepartments, useVentures } from '../../../organisation/api/organisation-api';
import { useCreateCalendar } from '../../api/calendars-api';
import styles from './CalendarPopovers.module.css';

const FORM_W = 420;

interface CalendarDraft {
  readonly name: string;
  readonly timezone: string;
  readonly workingDays: number;
  readonly startTime: string;
  readonly endTime: string;
  readonly ventureId: string | null;
  readonly departmentId: string | null;
}

/** Smart defaults: the standard week, so most calendars are a name and a save. */
const BLANK: CalendarDraft = {
  name: '',
  timezone: DEFAULT_CALENDAR.timezone,
  workingDays: DEFAULT_CALENDAR.workingDays,
  startTime: DEFAULT_CALENDAR.startTime,
  endTime: DEFAULT_CALENDAR.endTime,
  ventureId: null,
  departmentId: null,
};

/**
 * "Add calendar", opened beside its button (CLAUDE.md §5: pop-ups anchor to their trigger; it was a
 * centred dialog). The new calendar opens in the page once it is saved.
 */
export function AddCalendarButton({ onCreated }: { readonly onCreated: (id: string) => void }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  return (
    <>
      <Button ref={triggerRef} variant="solid" icon={Plus} aria-expanded={anchor !== null} onClick={toggle}>
        Add calendar
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={FORM_W} label="Add a calendar">
        {anchor && (
          <AddCalendarForm
            onCancel={close}
            onCreated={(id) => {
              close();
              onCreated(id);
            }}
          />
        )}
      </Popover>
    </>
  );
}

function AddCalendarForm({ onCancel, onCreated }: { readonly onCancel: () => void; readonly onCreated: (id: string) => void }): ReactNode {
  const create = useCreateCalendar();
  const ventures = useVentures();
  const departments = useDepartments();
  const showToast = useToasts((state) => state.show);
  // Mounted only while open, so the draft starts blank every time without an effect to clear it.
  const [draft, setDraft] = useState<CalendarDraft>(BLANK);
  const isReady = draft.name.trim() !== '' && draft.workingDays !== 0 && draft.endTime > draft.startTime;

  // A department implies its venture, so choosing one narrows the other rather than refusing a mismatch later.
  const departmentOptions = (departments.data ?? [])
    .filter((department) => draft.ventureId === null || department.ventureId === draft.ventureId)
    .map((department) => ({ value: department.id, label: department.name }));

  const submit = (): void => {
    if (!isReady || create.isPending) return;
    create.mutate(
      { ...draft, name: draft.name.trim() },
      {
        onSuccess: (calendar) => {
          showToast({ tone: 'success', message: `${calendar.name} added` });
          onCreated(calendar.id);
        },
        // What was typed stays, so the fix is one change and Add again.
        onError: (error) => showToast({ tone: 'danger', message: "The calendar wasn't added", detail: error.message }),
      },
    );
  };

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          submit();
        }
      }}
    >
      <div className={styles.head}>
        <h3 className={styles.title}>Add a calendar</h3>
        <p className={styles.lede}>Attach it to a venture or a department. Anything without one of its own inherits.</p>
      </div>

      <Field label="Name" isRequired>
        {({ inputId }) => <Input id={inputId} autoFocus placeholder="Engineering — UK week" maxLength={120} value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />}
      </Field>

      <div className={styles.pair}>
        <Field label="Venture" isOptional>
          {({ inputId }) => (
            <Select
              id={inputId}
              label="Venture"
              placeholder="Every venture"
              value={draft.ventureId}
              options={(ventures.data ?? []).map((venture) => ({ value: venture.id, label: venture.name }))}
              onChange={(ventureId) => setDraft({ ...draft, ventureId, departmentId: null })}
            />
          )}
        </Field>
        <Field label="Department" isOptional>
          {({ inputId }) => (
            <Select id={inputId} label="Department" placeholder="Whole venture" value={draft.departmentId} disabled={draft.ventureId === null} options={departmentOptions} onChange={(departmentId) => setDraft({ ...draft, departmentId })} />
          )}
        </Field>
      </div>

      <Field label="Working days">
        {({ inputId }) => <WorkingDayPicker id={inputId} value={draft.workingDays} onChange={(workingDays) => setDraft({ ...draft, workingDays })} />}
      </Field>

      <div className={styles.pair}>
        <Field label="Opens" isRequired>
          {({ inputId }) => <TimePicker id={inputId} label="Opens" value={draft.startTime} onChange={(startTime) => setDraft({ ...draft, startTime })} />}
        </Field>
        <Field label="Closes" isRequired error={draft.endTime > draft.startTime ? undefined : 'Later than the opening time.'}>
          {({ inputId }) => <TimePicker id={inputId} label="Closes" value={draft.endTime} onChange={(endTime) => setDraft({ ...draft, endTime })} />}
        </Field>
      </div>

      <Field label="Timezone">
        {({ inputId }) => (
          <Select id={inputId} label="Timezone" value={draft.timezone} options={SUPPORTED_TIMEZONES.map((zone) => ({ value: zone.value, label: zone.label }))} onChange={(timezone) => setDraft({ ...draft, timezone })} />
        )}
      </Field>

      <div className={styles.actions}>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="solid" isPending={create.isPending} disabled={!isReady}>
          Add calendar
        </Button>
      </div>
    </form>
  );
}
